import { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            error_callback?: (error: { type?: string; message?: string }) => void;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: Record<string, string | number | boolean>,
          ) => void;
        };
      };
    };
  }
}

interface GoogleSignInButtonProps {
  clientId: string;
  onCredential: (idToken: string) => void;
  disabled?: boolean;
  onError?: (message: string) => void;
}

const SCRIPT_ID = 'google-gsi-client';
let gsiClientId: string | null = null;

export default function GoogleSignInButton({
  clientId,
  onCredential,
  disabled,
  onError,
}: GoogleSignInButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onCredentialRef = useRef(onCredential);
  const onErrorRef = useRef(onError);
  const [ready, setReady] = useState(false);
  onCredentialRef.current = onCredential;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!clientId || disabled) return;
    let cancelled = false;

    const init = () => {
      if (cancelled || !window.google?.accounts?.id || !containerRef.current) return;
      if (gsiClientId !== clientId) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            if (response.credential) onCredentialRef.current(response.credential);
          },
          error_callback: (error) => {
            const msg = error?.message || error?.type || 'Google sign-in failed';
            onErrorRef.current?.(
              /origin/i.test(msg)
                ? `Google does not allow this host yet (${window.location.origin}). Use email and password, or ask BirdMan to add this origin.`
                : msg,
            );
          },
        });
        gsiClientId = clientId;
      }
      containerRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(containerRef.current, {
        theme: 'outline',
        size: 'large',
        width: 320,
        text: 'continue_with',
        shape: 'rectangular',
      });
      setReady(true);
    };

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      if (window.google?.accounts?.id) init();
      else existing.addEventListener('load', init);
      return () => {
        cancelled = true;
        existing.removeEventListener('load', init);
      };
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = init;
    document.head.appendChild(script);

    return () => {
      cancelled = true;
      script.onload = null;
    };
  }, [clientId, disabled]);

  if (!clientId) return null;

  return (
    <div className={disabled ? 'pointer-events-none opacity-50' : ''}>
      <div ref={containerRef} className="flex min-h-[44px] justify-center" />
      {!ready && (
        <p className="mt-1 text-center text-xs text-slate-400">Loading Google sign-in…</p>
      )}
    </div>
  );
}
